
"use client";

import React, { useEffect, useRef } from "react";
import { FaqSection } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";
import VideoContent from "@/components/VideoContent";
import type { Collapse } from "bootstrap";
import QuestionIcon from "@/assets/images/shared/question-card-icon.svg";
// import stripeImg from "@/assets/images/purchases/powered-by-stripe.svg";
import Image from "next/image";

interface FaqSectionProps {
  blok?: FaqSection & SbBlokData;
}

const FaqSectionComponent: React.FC<FaqSectionProps> = ({ blok }) => {
  const collapseInstancesRef = useRef<Collapse[]>([]);

  useEffect(() => {
    let mounted = true;

    const initBootstrap = async () => {
      if (typeof window !== "undefined") {
        try {
          const bootstrap = await import("bootstrap");
          if (!mounted) return;

          // Cleanup previous instances
          collapseInstancesRef.current.forEach((instance) => {
            instance.dispose();
          });
          collapseInstancesRef.current = [];

          // Initialize new instances
          document
            .querySelectorAll(".accordion-collapse")
            .forEach((element) => {
              const collapseInstance = new bootstrap.Collapse(element, {
                toggle: false,
              });
              collapseInstancesRef.current.push(collapseInstance);
            });
        } catch (error) {
          console.error("Error initializing Bootstrap:", error);
        }
      }
    };

    initBootstrap();

    // Cleanup function
    return () => {
      mounted = false;
      collapseInstancesRef.current.forEach((instance) => {
        instance.dispose();
      });
      collapseInstancesRef.current = [];
    };
  }, []); // Empty dependency array since we only want this to run once on mount

  if (!blok) return null;

  const { title, faqs, type = "default" } = blok;


  const stripHtml = (html: string) => {
    return html.replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .trim();
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs?.map(faq => ({
      "@type": "Question",
      "name": faq.title,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": stripHtml(renderRichText(faq.description))
      }
    }))
  };

  const renderContent = (description: any) => {
    if (!description) return null;

    const hasVideoContent = description?.content?.some(
      (item: any) =>
        item.type === "blok" &&
        item.attrs?.body?.some(
          (block: any) => block.component === "video_content"
        )
    );

    if (!hasVideoContent) {
      return (
        <div
          dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
        />
      );
    }

    return (
      <>
        {description.content.map((item: any, index: number) => {
          if (item.type === "blok" && item.attrs?.body) {
            return item.attrs.body.map((block: any) => {
              if (block.component === "video_content") {
                return <VideoContent key={block._uid} blok={block} />;
              }
              return null;
            });
          }
          if (item.type === "paragraph") {
            return (
              <div
                key={index}
                dangerouslySetInnerHTML={{
                  __html: renderRichText({ type: "doc", content: [item] }),
                }}
              />
            );
          }
          return null;
        })}
      </>
    );
  };

  if (type === "blog") {
    return (
      <>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <div className="two-thirds-centered" {...storyblokEditable(blok)}>
          {title && (
            <h2
              className="mb-4 text-center"
              id={title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
            >
              {title}
            </h2>
          )}
          {faqs?.map((faq) => (
            <div
              key={faq._uid}
              className="card-blog-question mb-4"
              itemScope
              itemType="http://schema.org/Question"
            >
              <div className="card-blog-question-header">
                <Image
                  src={QuestionIcon}
                  width={60}
                  height={60}
                  alt="Question icon"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
                <h3 itemProp="name">{faq.title}</h3>
              </div>
              <div
                className="card-blog-question-content"
                itemProp="suggestedAnswer acceptedAnswer"
                itemScope
                itemType="http://schema.org/Answer"
              >
                {renderContent(faq.description)}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  const midPoint = Math.ceil((faqs?.length || 0) / 2);
  const leftColumnFaqs = faqs?.slice(0, midPoint) || [];
  const rightColumnFaqs = faqs?.slice(midPoint) || [];

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <section
        className="bg-light component-faq_columns py-4 py-md-5"
        {...storyblokEditable(blok)}
      >
        <div className="container">
          {title && (
            <h2
              className="text-center text-primary mb-5"
              id={title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
            >
              {title}
            </h2>
          )}
          <div className="faq-columns row gy-3" id="js-faqs">
            <div className="col-lg-12">
              {leftColumnFaqs.map((faq, index) => (
                <div
                  key={faq._uid}
                  className="faq-column card card-lighter p-2 px-3 mb-3"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <h3 className="h4 text-primary" itemProp="name">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#faqleft-${index}`}
                      aria-expanded="false"
                      aria-controls={`faqleft-${index}`}
                    >
                      {faq.title}
                    </button>
                  </h3>
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    id={`faqleft-${index}`}
                    className="accordion-collapse collapse"
                    data-bs-parent="#js-faqs"
                  >
                    <div className="accordion-body" itemProp="text">
                      {renderContent(faq.description)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="col-lg-12">
              {rightColumnFaqs.map((faq, index) => (
                <div
                  key={faq._uid}
                  className="faq-column card card-lighter p-2 px-3 mb-3"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <h3 className="h4 text-primary" itemProp="name">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#faqright-${index}`}
                      aria-expanded="false"
                      aria-controls={`faqright-${index}`}
                    >
                      {faq.title}
                    </button>
                  </h3>
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    id={`faqright-${index}`}
                    className="accordion-collapse collapse"
                    data-bs-parent="#js-faqs"
                  >
                    <div className="accordion-body" itemProp="text">
                      {renderContent(faq.description)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FaqSectionComponent;